import React, { JSX } from 'react';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import {
  Stack,
  Text,
  withTranslation,
  List,
  ContactForm,
  Button,
  formatNumber,
} from '@piwikpro/ui-components';
import {
  withInject,
  ConfigService,
} from '@piwikpro/platform';
import { OrganizationState } from '@piwikpro/organization-crate';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { ModulesIds } from '@piwikpro/organization-crate/redux/details';
import { Notifications } from '@piwikpro/notification-crate/Notifications';

const mapStateToProps = (state: {
  session: SessionState
  organization: OrganizationState
}) => ({
  license: state.organization.details.license?.current,
  session: state.session,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  notifications: Notifications
}

interface Props extends ReduxProps, InjectedProps{
  t: TFunction
}

export const LicenseDetailsAccount: React.FC<Props> = ({
  license,
  session,
  config,
  notifications,
  t,
}) => {
  const licenseModules = license?.modules || [];
  const availableModules = config.get('AVAILABLE_MODULES');

  const ppasModules = () => {
    const availableModulesList = Object.keys(availableModules).filter(module => module !== 'consent_manager');

    return licenseModules
      .filter(module => availableModulesList.includes(module))
      .map(module => (
        {
          id: module,
          label: availableModules[module].label,
        }
      ));
  };

  const getMissingModules = () => Object.keys(availableModules).filter(module => module !== 'consent_manager')
    .filter(ppasModule =>
      !licenseModules.includes(ppasModule as ModulesIds))
    .map(ppasModule => availableModules[ppasModule].label);

  const getCiCmpDomainsLimit = () => {
    const ciDomainsLimit = license?.plan.administration?.ci_domains_limit;

    if (ciDomainsLimit === -1) {
      return t('platform.license.details.general-info.unlimited');
    }

    return typeof ciDomainsLimit === 'number' ? formatNumber(ciDomainsLimit) : '';
  };

  const getOrgDetailsList = () => [
    {
      title: t('platform.license.details.organizationName.label'),
      value: (
        <div id="platform-license-organization-name">
          <Text as="p">{session.organizationName}</Text>
        </div>
      ),
    },
  ];
  const getDomainDetailsList = () => {
    if (license?.plan.administration?.ci_domains_limit === -1) {
      return [];
    }

    return [
      {
        title: t('platform.license.details.ciDomainsLimit.label'),
        value: (
          <div id="platform-license-ci-domains-limit">
            <Text as="p">{getCiCmpDomainsLimit()}</Text>
          </div>
        ),
      },
    ];
  };

  const getModulesList = () => [
    {
      title: t('platform.license.modules'),
      value: (
        <div id="platform-license-modules" data-ppuc-id="platform-license-modules">
          <Stack
            vertical
            horizontalAlignment="right"
            spacing="extraNarrow"
          >
            <Stack.Item>
              <Stack horizontalAlignment="right" spacing="extraNarrow">
                {ppasModules().map((ppasModule, index) => (
                  <Stack.Item>
                    <Text as="p" color={license && license?.modules.includes(ppasModule.id as ModulesIds) ? undefined : 'InkLighter'}>
                      {ppasModule.label}
                      {index !== ppasModules().length - 1 && ','}
                    </Text>
                  </Stack.Item>
                ))}
              </Stack>
            </Stack.Item>
          </Stack>
        </div>
      ),
    },
  ];

  const definitionListItems = [] as { title: string, value: JSX.Element }[];
  definitionListItems.push(...getOrgDetailsList());
  definitionListItems.push(...getDomainDetailsList());
  const hasMissingModules = getMissingModules().length > 0;

  if (hasMissingModules) {
    definitionListItems.push(...getModulesList());
  }

  return (
    <Stack vertical>
      <Stack.Item fill>
        <Stack vertical>
          {definitionListItems.map(item => (
            <Stack.Item key={item.title} fill>
              <Stack>
                <Stack.Item fill>
                  <Text as="p" weight={600} dataId="Label">{item.title}</Text>
                </Stack.Item>
                <Stack.Item>{item.value}</Stack.Item>
              </Stack>
            </Stack.Item>
          ))}
        </Stack>
      </Stack.Item>
      {hasMissingModules && (
        <Stack.Item fill previousSpacing="default">
          <div style={{ marginTop: '-4px' }}>
            <List columns={[
              {
                size: 1,
                align: 'left',
              },
              {
                size: 0,
                align: 'right',
              },
              {
                size: 0,
                align: 'right',
              },
            ]}
            >
              <List.Item>
                <Text as="p" weight={600} color="InkLighter" dataId="Label">{t('platform.license.not-included.title')}</Text>
                <Stack.Item fill>
                  <Stack verticalAlignment="center">
                    <Stack.Item>
                      <Text as="p" color="InkLighter">
                        <div style={{ textAlign: 'right' }}>
                          {getMissingModules().join(', ')}
                        </div>
                      </Text>
                    </Stack.Item>
                    <Stack.Item>
                      <ContactForm
                        type="sales"
                        onSubmitSuccess={({ message }) => {
                          notifications.push({
                            text: message,
                            type: 'success',
                            autoCloseSeconds: 5,
                          });
                        }}
                        onSubmitFail={({ message }) => {
                          notifications.push({
                            text: message,
                            type: 'error',
                            autoCloseSeconds: 5,
                          });
                        }}
                      >
                        {({ open }) => (
                          <Button
                            text={t('platform.license.contact-us.button')}
                            appearance="simple"
                            onClick={open}
                          />
                        )}
                      </ContactForm>
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </List.Item>
            </List>
          </div>
        </Stack.Item>
      )}
      <Stack.Item previousSpacing="narrow" />
    </Stack>
  );
};

export default connector(withInject<InjectedProps>({
  config: 'config',
  notifications: 'NotificationCrate.notifications',
})(withTranslation(['administration'])(LicenseDetailsAccount)));
